// src/components/admin/TorreDeControl/modals/PaymentsModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { X, DollarSign } from 'lucide-react';
import axios from 'axios';
import {
  DELIVERY_ADDONS,
  DELIVERY_SERVICE_LEVELS,
  calculateDeliveryPricing,
  normalizeAddonCodes,
  normalizeServiceCode,
} from '../../../../constants/deliveryPricing';

const API_URL = process.env.REACT_APP_API_URL;

function formatCurrency(value) {
  const amount = Number(value || 0);
  return `S/ ${amount.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function PaymentsModal({ item, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    charge: '',
    charged: '',
    fastmax_transfer_amount: '',
    provider_transfer_amount: '',
    fastmax_exchange_amount: '',
    service_fee_enabled: true,
    service_level_code: 'classic',
    addon_codes: [],
    manual_adjustment_amount: '',
    manual_adjustment_label: '',
    pricing_update_reason: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [pricingTouched, setPricingTouched] = useState(false);

  useEffect(() => {
    if (item) {
      const currentPricingTotal = item.pricing_total_cents !== undefined && item.pricing_total_cents !== null
        ? Number(item.pricing_total_cents) / 100
        : item.pricing_total;

      setFormData({
        charge: item.charge || '',
        charged: item.charged || '',
        fastmax_transfer_amount: item.fastmax_transfer_amount || '',
        provider_transfer_amount: item.provider_transfer_amount || '',
        fastmax_exchange_amount: item.fastmax_exchange_amount || '',
        service_fee_enabled: currentPricingTotal !== 0,
        service_level_code: normalizeServiceCode(item.service_level_code || 'classic'),
        addon_codes: normalizeAddonCodes(item.selected_addon_codes || []),
        manual_adjustment_amount: '',
        manual_adjustment_label: '',
        pricing_update_reason: '',
      });
      setPricingTouched(false);
    }
  }, [item]);

  const selectedAddonCodes = useMemo(
    () => normalizeAddonCodes(formData.addon_codes),
    [formData.addon_codes]
  );

  const pricingPreview = useMemo(() => (
    calculateDeliveryPricing(formData.service_level_code, selectedAddonCodes)
  ), [formData.service_level_code, selectedAddonCodes]);

  const manualAdjustment = Number(formData.manual_adjustment_amount || 0);
  const newPricingTotal = formData.service_fee_enabled
    ? Math.max(0, pricingPreview.total + (Number.isFinite(manualAdjustment) ? manualAdjustment : 0))
    : 0;
  const currentPricingTotal = item?.pricing_total_cents !== undefined && item?.pricing_total_cents !== null
    ? Number(item.pricing_total_cents) / 100
    : item?.pricing_total;
  const pricingDifference = newPricingTotal - Number(currentPricingTotal || 0);

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        charge: formData.charge,
        charged: formData.charged,
        fastmax_transfer_amount: formData.fastmax_transfer_amount,
        provider_transfer_amount: formData.provider_transfer_amount,
        fastmax_exchange_amount: formData.fastmax_exchange_amount,
      };

      if (pricingTouched) {
        Object.assign(payload, {
          service_fee_enabled: formData.service_fee_enabled,
          service_level_code: normalizeServiceCode(formData.service_level_code),
          addon_codes: selectedAddonCodes,
          manual_adjustment_amount: formData.manual_adjustment_amount,
          manual_adjustment_label: formData.manual_adjustment_label,
          pricing_update_reason: formData.pricing_update_reason,
        });
      }

      await axios.put(`${API_URL}/api/admin/delivery-point/${item.id}/payments`, payload);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating payments:', error);
      alert(error.response?.data?.error || 'Error al actualizar pagos');
    } finally {
      setIsLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    if ([
      'service_fee_enabled',
      'manual_adjustment_amount',
      'manual_adjustment_label',
      'pricing_update_reason',
    ].includes(name)) {
      setPricingTouched(true);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  function handleServiceSelect(serviceCode) {
    setPricingTouched(true);
    setFormData((prev) => ({
      ...prev,
      service_level_code: serviceCode,
    }));
  }

  function toggleAddon(addonCode) {
    setPricingTouched(true);
    setFormData((prev) => {
      const current = normalizeAddonCodes(prev.addon_codes);
      const exists = current.includes(addonCode);
      return {
        ...prev,
        addon_codes: exists
          ? current.filter((code) => code !== addonCode)
          : [...current, addonCode],
      };
    });
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-hidden">
        <div className="bg-[#B1336E] px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg flex items-center gap-2 font-montserrat">
            <DollarSign className="w-5 h-5" />
            Actualizar Pagos y Tarifa
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[calc(92vh-76px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Cargo (S/)
              </label>
              <input
                type="number"
                step="0.01"
                name="charge"
                value={formData.charge}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Cobrado (S/)
              </label>
              <input
                type="number"
                step="0.01"
                name="charged"
                value={formData.charged}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Transfer Fast Max (S/)
              </label>
              <input
                type="number"
                step="0.01"
                name="fastmax_transfer_amount"
                value={formData.fastmax_transfer_amount}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Transfer Proveedor (S/)
              </label>
              <input
                type="number"
                step="0.01"
                name="provider_transfer_amount"
                value={formData.provider_transfer_amount}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Vuelto Fast Max (S/)
              </label>
              <input
                type="number"
                step="0.01"
                name="fastmax_exchange_amount"
                value={formData.fastmax_exchange_amount}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
              />
            </div>
          </div>

          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h4 className="text-base font-bold text-slate-800 font-montserrat">
                  Tarifa Fast Max de esta entrega
                </h4>
                <p className="text-xs text-slate-500 font-montserrat">
                  Para casos especiales, usa el ajuste manual en vez de los antiguos checkboxes de S/15 o S/20.
                </p>
              </div>
              <label className="inline-flex items-center gap-3 cursor-pointer bg-white border border-slate-200 px-3 py-2 rounded-lg">
                <input
                  type="checkbox"
                  name="service_fee_enabled"
                  checked={formData.service_fee_enabled}
                  onChange={handleChange}
                  className="w-5 h-5 text-[#B1336E] border-slate-300 rounded focus:ring-2 focus:ring-[#B1336E]/20"
                />
                <span className="text-sm font-bold text-slate-700 font-montserrat">
                  Cobrar tarifa
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {DELIVERY_SERVICE_LEVELS.map((service) => {
                const active = formData.service_level_code === service.code;
                return (
                  <button
                    key={service.code}
                    type="button"
                    disabled={!formData.service_fee_enabled}
                    onClick={() => handleServiceSelect(service.code)}
                    className={`text-left rounded-xl border-2 p-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      active
                        ? 'border-[#B1336E] bg-[#B1336E]/5'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-slate-800 font-montserrat">{service.label}</span>
                      <span className="font-bold text-[#B1336E] font-montserrat">{service.priceLabel}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 font-montserrat">{service.shortDescription}</p>
                  </button>
                );
              })}
            </div>

            <div>
              <p className="text-xs font-bold text-slate-500 uppercase mb-2 font-montserrat">Add-ons</p>
              <div className="flex flex-wrap gap-2">
                {DELIVERY_ADDONS.map((addon) => {
                  const active = selectedAddonCodes.includes(addon.code);
                  return (
                    <button
                      key={addon.code}
                      type="button"
                      disabled={!formData.service_fee_enabled}
                      onClick={() => toggleAddon(addon.code)}
                      className={`px-3 py-2 rounded-full border text-sm font-bold font-montserrat transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        active
                          ? 'bg-[#B1336E] text-white border-[#B1336E]'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {addon.label} {addon.priceLabel}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Ajuste manual admin (S/)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="manual_adjustment_amount"
                  value={formData.manual_adjustment_amount}
                  onChange={handleChange}
                  disabled={!formData.service_fee_enabled}
                  placeholder="Ej: 5.10 o -3.00"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat disabled:bg-slate-100"
                />
                <p className="text-xs text-slate-400 mt-1 font-montserrat">
                  Para cobrar S/15 desde Clásico, usa +5.10. Para S/20 desde Premium, usa +0.10.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Etiqueta del ajuste
                </label>
                <input
                  type="text"
                  name="manual_adjustment_label"
                  value={formData.manual_adjustment_label}
                  onChange={handleChange}
                  disabled={!formData.service_fee_enabled}
                  placeholder="Ej: Zona especial, coordinación extra"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat disabled:bg-slate-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Motivo interno del cambio
              </label>
              <textarea
                name="pricing_update_reason"
                value={formData.pricing_update_reason}
                onChange={handleChange}
                rows={2}
                placeholder="Ej: Usuario eligió por error, zona requiere coordinación adicional, decisión interna comercial..."
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-500 font-bold uppercase font-montserrat">Tarifa actual</p>
                <p className="text-lg font-bold text-slate-800 font-montserrat">
                  {currentPricingTotal === null || currentPricingTotal === undefined ? 'Sin snapshot' : formatCurrency(currentPricingTotal)}
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-500 font-bold uppercase font-montserrat">Nueva tarifa</p>
                <p className="text-lg font-bold text-[#B1336E] font-montserrat">{formatCurrency(newPricingTotal)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-500 font-bold uppercase font-montserrat">Diferencia</p>
                <p className={`text-lg font-bold font-montserrat ${pricingDifference >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {pricingDifference >= 0 ? '+' : '-'}{formatCurrency(Math.abs(pricingDifference))}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-[#B1336E] hover:bg-[#B1336E]/90 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PaymentsModal;
